import apiClient from './api';
import {
  NoticeItem,
  NoticeListResponse,
  NoticeDetailResponse,
  CreateNoticeRequest,
  UpdateNoticeRequest,
  ApproveNoticeRequest,
  BulkDeleteNoticesRequest,
  BulkUpdateNoticeExposureRequest,
  NoticeFilterOptions,
  NoticeExposureType,
  NoticeApprovalStatusType
} from '../types/notice';

class NoticeService {
  // 공지사항 목록 조회
  async getNoticeList(options: NoticeFilterOptions = {}): Promise<NoticeListResponse> {
    try {
      const params = new URLSearchParams();

      if (options.page) params.append('page', options.page.toString());
      if (options.limit) params.append('limit', options.limit.toString());
      if (options.search) params.append('search', options.search);
      if (options.layout && options.layout !== 'all') {
        params.append('layout', options.layout);
      }
      if (options.exposure && options.exposure !== 'all') {
        params.append('exposure', options.exposure);
      }
      if (options.approvalStatus && options.approvalStatus !== 'all') {
        params.append('approvalStatus', options.approvalStatus);
      }

      const response = await apiClient.get(`/notices?${params.toString()}`);
      return response.data;
    } catch (error) {
      console.error('공지사항 목록 조회 실패:', error);
      throw new Error('공지사항 목록을 불러오는데 실패했습니다.');
    }
  }

  // 공지사항 상세 조회
  async getNoticeDetail(id: number): Promise<NoticeItem> {
    try {
      const response = await apiClient.get(`/notices/${id}`);
      return response.data.data;
    } catch (error) {
      console.error('공지사항 상세 조회 실패:', error);
      throw new Error('공지사항 상세 정보를 불러오는데 실패했습니다.');
    }
  }

  // 공지사항 생성
  async createNotice(noticeData: CreateNoticeRequest): Promise<NoticeItem> {
    try {
      const response = await apiClient.post('/notices', noticeData);
      return response.data.data;
    } catch (error) {
      console.error('공지사항 생성 실패:', error);
      throw new Error('공지사항 생성에 실패했습니다.');
    }
  }

  // 공지사항 수정
  async updateNotice(noticeData: UpdateNoticeRequest): Promise<NoticeItem> {
    try {
      const { id, ...updateData } = noticeData;
      const response = await apiClient.put(`/notices/${id}`, updateData);
      return response.data.data;
    } catch (error) {
      console.error('공지사항 수정 실패:', error);
      throw new Error('공지사항 수정에 실패했습니다.');
    }
  }

  // 공지사항 삭제
  async deleteNotice(id: number): Promise<void> {
    try {
      await apiClient.delete(`/notices/${id}`);
    } catch (error) {
      console.error('공지사항 삭제 실패:', error);
      throw new Error('공지사항 삭제에 실패했습니다.');
    }
  }

  // 공지사항 일괄 삭제
  async bulkDeleteNotices(ids: number[]): Promise<{ deletedCount: number }> {
    try {
      const response = await apiClient.delete('/notices/bulk-delete', {
        data: { ids }
      });
      return response.data.data;
    } catch (error) {
      console.error('공지사항 일괄 삭제 실패:', error);
      throw new Error('선택한 공지사항 삭제에 실패했습니다.');
    }
  }

  // 공지사항 일괄 노출/비노출 상태 변경
  async bulkUpdateExposure(
    ids: number[],
    exposure: NoticeExposureType
  ): Promise<{ updatedCount: number }> {
    try {
      const response = await apiClient.patch('/notices/bulk-update-exposure', {
        ids,
        exposure
      });
      return response.data.data;
    } catch (error) {
      console.error('공지사항 일괄 노출 상태 변경 실패:', error);
      throw new Error('선택한 공지사항의 노출 상태 변경에 실패했습니다.');
    }
  }

  // 공지사항 승인/반려/취소
  async approveNotice(id: number, approvalStatus: 'approved' | 'rejected' | 'pending', rejectionReason?: string): Promise<NoticeItem> {
    try {
      const response = await apiClient.patch(`/notices/${id}/approve`, {
        approvalStatus,
        rejectionReason
      });
      return response.data.data;
    } catch (error) {
      console.error('공지사항 승인 처리 실패:', error);
      throw new Error('공지사항 승인 처리에 실패했습니다.');
    }
  }

  // 공지사항 검색
  async searchNotices(
    searchKeyword: string,
    page: number = 1,
    limit: number = 10
  ): Promise<NoticeListResponse> {
    return this.getNoticeList({
      search: searchKeyword,
      page,
      limit
    });
  }

  // 레이아웃별 공지사항 조회
  async getNoticesByLayout(
    layout: '1' | '2' | 'all',
    page: number = 1,
    limit: number = 10
  ): Promise<NoticeListResponse> {
    return this.getNoticeList({
      layout,
      page,
      limit
    });
  }

  // 노출 상태별 공지사항 조회
  async getNoticesByExposure(
    exposure: NoticeExposureType | 'all',
    page: number = 1,
    limit: number = 10
  ): Promise<NoticeListResponse> {
    return this.getNoticeList({
      exposure,
      page,
      limit
    });
  }

  // 승인 상태별 공지사항 조회
  async getNoticesByApprovalStatus(
    approvalStatus: NoticeApprovalStatusType | 'all',
    page: number = 1,
    limit: number = 10
  ): Promise<NoticeListResponse> {
    return this.getNoticeList({
      approvalStatus,
      page,
      limit
    });
  }

  // 공지사항 통계 조회
  async getNoticeStats(): Promise<{
    total: number;
    exposed: number;
    hidden: number;
    pending: number;
    approved: number;
    rejected: number;
    recentlyCreated: number;
    activeNotices: number;
  }> {
    try {
      // 전체 공지사항 조회 (limit을 크게 설정)
      const allNotices = await this.getNoticeList({ limit: 1000 });
      const notices = allNotices.data.notices;

      // 최근 생성 기준 (7일 이내)
      const weekAgo = new Date();
      weekAgo.setDate(weekAgo.getDate() - 7);

      // 현재 활성 공지사항 (노출 + 승인완료 + 기간 내)
      const now = new Date();
      const activeNotices = notices.filter(notice => {
        const startDate = new Date(notice.startDate);
        const endDate = new Date(notice.endDate);
        return (
          notice.exposure === 'show' &&
          notice.approvalStatus === 'approved' &&
          now >= startDate &&
          now <= endDate
        );
      });

      const stats = {
        total: notices.length,
        exposed: notices.filter(n => n.exposure === 'show').length,
        hidden: notices.filter(n => n.exposure === 'hide').length,
        pending: notices.filter(n => n.approvalStatus === 'pending').length,
        approved: notices.filter(n => n.approvalStatus === 'approved').length,
        rejected: notices.filter(n => n.approvalStatus === 'rejected').length,
        recentlyCreated: notices.filter(n =>
          new Date(n.createdAt) > weekAgo
        ).length,
        activeNotices: activeNotices.length
      };

      return stats;
    } catch (error) {
      console.error('공지사항 통계 조회 실패:', error);
      return {
        total: 0,
        exposed: 0,
        hidden: 0,
        pending: 0,
        approved: 0,
        rejected: 0,
        recentlyCreated: 0,
        activeNotices: 0
      };
    }
  }

  // 공지사항 유효성 검사
  validateNoticeData(noticeData: Partial<CreateNoticeRequest | UpdateNoticeRequest>): string[] {
    const errors: string[] = [];

    if (noticeData.title && noticeData.title.trim().length === 0) {
      errors.push('제목을 입력해주세요.');
    }

    if (noticeData.title && noticeData.title.length > 255) {
      errors.push('제목은 255자를 초과할 수 없습니다.');
    }

    if (noticeData.startDate && noticeData.endDate) {
      const startDate = new Date(noticeData.startDate);
      const endDate = new Date(noticeData.endDate);

      if (startDate >= endDate) {
        errors.push('종료일은 시작일보다 늦어야 합니다.');
      }
    }

    if (noticeData.contents) {
      if (noticeData.contents.length === 0) {
        errors.push('공지사항 내용을 최소 1개 이상 입력해주세요.');
      }

      noticeData.contents.forEach((content, index) => {
        if (!content.subtitle || content.subtitle.trim().length === 0) {
          errors.push(`${index + 1}번째 내용의 소제목을 입력해주세요.`);
        }
        if (!content.detail || content.detail.trim().length === 0) {
          errors.push(`${index + 1}번째 내용의 세부정보를 입력해주세요.`);
        }
      });
    }

    return errors;
  }
}

export default new NoticeService();